/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { useRef } from 'preact/hooks';
import { useDrag } from './hooks/useDrag';
import type {
  horizontalScrollbarStyles,
  verticalScrollbarStyles
} from './themes/TimeComponentStyles.css';

type HorizontalThumbProps = {
  thumbWidth: number;
  thumbHeight?: undefined;
  thumbTop?: undefined;
  thumbLeft?: number;
  thumbRight?: number;
  styles: typeof horizontalScrollbarStyles;
};

type VerticalThumbProps = {
  thumbWidth?: undefined;
  thumbHeight: number;
  thumbTop: number;
  thumbLeft?: undefined;
  thumbRight?: undefined;
  styles: typeof verticalScrollbarStyles;
};

type Props = (HorizontalThumbProps | VerticalThumbProps) & {
  draggableRegion: { xMin: number; xMax: number; yMin: number; yMax: number };
  processDragRangeXChange?: (x: number, thumbOffsetX: number) => void;
  processDragRangeYChange?: (y: number, thumbOffsetY: number) => void;
  processClickRangeXChange?: (x: number) => void;
  processClickRangeYChange?: (y: number) => void;
  width: number;
  height: number;
};

export function TimeComponentScrollbar({
  thumbWidth,
  thumbHeight,
  thumbTop,
  thumbLeft,
  thumbRight,
  draggableRegion,
  processDragRangeXChange,
  processDragRangeYChange,
  processClickRangeXChange,
  processClickRangeYChange,
  styles,
  width,
  height
}: Props) {
  // Scrollbar is draggable to change the range
  const ref = useRef<HTMLDivElement>(null);
  const dragProps = useDrag({
    captureTargetRef: ref,
    draggableRegion,
    onDragMove: ({ x, y, regionOffsetX, regionOffsetY }) => {
      processDragRangeXChange?.(x, regionOffsetX);
      processDragRangeYChange?.(y, regionOffsetY);
    }
  });

  // Scrollbar is also clickable to jump to an arbitrary range
  const handleClick = (e: MouseEvent) => {
    const x = e.offsetX;
    const y = e.offsetY;
    const { xMin, xMax, yMin, yMax } = draggableRegion;
    const inDraggableRegion = x >= xMin && x <= xMax && y >= yMin && y <= yMax;
    if (inDraggableRegion) return;
    processClickRangeXChange?.(x);
    processClickRangeYChange?.(y);
  };

  return (
    <div
      ref={ref}
      class={styles.container}
      style={{ width, height }}
      onClick={handleClick}
      {...dragProps}>
      <div class={styles.track}>
        <div
          class={styles.thumb}
          style={{
            width: thumbWidth,
            height: thumbHeight,
            top: thumbTop,
            left: thumbLeft,
            right: thumbRight
          }}
        />
      </div>
    </div>
  );
}
